import React, { useEffect, useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import {
  Button,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@material-ui/core";
import useStyles from "./search-panel.styles";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "app/store";
import {
  clearfilterFlights,
  filterFlights,
} from "../store/flights-list/filter-flights.slice";
import { fetchAllAirports } from "../../configurations/store/airport/airports-list.slice";
import { Airport } from "app/types/service-types/AirportServiceTypes";
import { useTranslation } from "react-i18next";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import DateUtils from "../../../utils/DateUtils";
import { Autocomplete } from "@material-ui/lab";
import { errorMsg, successMsg } from "app/utils/MessageUtils";
import { updateRoutePrices } from "app/services/flight.services";
import { useHistory, useLocation } from "react-router-dom";
import { UpdateRoutePricesParams } from "app/types/service-types/FlightTypes";

const SearchPanel = () => {
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filters = useSelector(
    (state: AppState) => state.flights.flightsList.filterFlights,
  );
  const airports = useSelector(
    (state: AppState) =>
      state.configurations.airport.airportsList.airports.content,
  );

  const [fromDate, setSelectedFromDate] = useState<any>(
    filters.fromDate ? DateUtils.convertDateToIso(filters.fromDate) : null,
  );
  const [toDate, setSelectedToDate] = useState<any>(
    filters.toDate ? DateUtils.convertDateToIso(filters.toDate) : null,
  );

  const isLocationMainFlightList = useMemo(() => {
    return location.pathname === "/flights/flights-list";
  }, [location]);

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [priceAdjustment, setPriceAdjustment] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);

  const depAirport: Airport | undefined = airports?.find(
    (a: Airport) => a.id === filters.departureAirportId,
  );
  const arrAirport: Airport | undefined = airports?.find(
    (a: Airport) => a.id === filters.arrivalAirportId,
  );

  const handleChange = (e: any) => {
    dispatch(
      filterFlights({
        ...filters,
        page: 0,
        [e.target.name]: e.target.value,
      }),
    );
  };

  useEffect(() => {
    dispatch(fetchAllAirports({ size: 9999 }));
  }, [dispatch]);

  useEffect(() => {
    setSelectedFromDate(
      filters.fromDate ? DateUtils.convertDateToIso(filters.fromDate) : null,
    );
  }, [filters.fromDate]);

  useEffect(() => {
    setSelectedToDate(
      filters.toDate ? DateUtils.convertDateToIso(filters.toDate) : null,
    );
  }, [filters.toDate]);

  // NEW: submit handler
  const handleBulkUpdate = async () => {
    if (priceAdjustment === "" || priceAdjustment === null) {
      dispatch(errorMsg("Please enter a price adjustment."));
      return;
    }

    if (!filters.fromDate || !filters.toDate) {
      dispatch(errorMsg("Please select a date range first."));
      return;
    }

    const fromDateStr = DateUtils.convertDate(filters.fromDate); // -> dd/MM/yyyy
    const toDateStr = DateUtils.convertDate(filters.toDate); // -> dd/MM/yyyy

    const params: UpdateRoutePricesParams = {
      fromDate: fromDateStr,
      toDate: toDateStr,
      priceAdjustment: Number(priceAdjustment),
      ...(depAirport ? { departureAirportCode: depAirport.code } : {}),
      ...(arrAirport ? { arrivalAirportCode: arrAirport.code } : {}),
    };

    try {
      setSubmitting(true);
      await updateRoutePrices(params);

      dispatch(successMsg("Prices updated successfully."));
      setUpdateModalOpen(false);
      setPriceAdjustment("");
      // refresh current list with same filters
      dispatch(filterFlights({ ...filters, page: 0 }));
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        "Something went wrong.";
      dispatch(errorMsg(message));
    } finally {
      setSubmitting(false);
    }
  };

  const canOpenModal = Boolean(filters.fromDate && filters.toDate);

  return (
    <Grid container className={classes.root}>
      {/* departure */}
      <div>
        <FormControl
          size="small"
          variant="outlined"
          className={classes.formControl}
        >
          <div style={{ position: "relative" }}>
            <Autocomplete
              size="small"
              options={airports.filter(
                (a: Airport) => a.id !== filters.arrivalAirportId,
              )}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              value={
                airports.find((a) => a.id === filters.departureAirportId) ||
                null
              }
              onChange={(e, value) =>
                handleChange({
                  target: {
                    name: "departureAirportId",
                    value: value ? value.id : "",
                  },
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "app.main.flights.searchPanel.inputLabel1",
                    "Departure from the airport",
                  )}
                  variant="outlined"
                />
              )}
            />
          </div>
        </FormControl>
      </div>

      {/* arrival */}
      <div>
        <FormControl
          size="small"
          variant="outlined"
          className={classes.formControl}
        >
          <div style={{ position: "relative" }}>
            <Autocomplete
              size="small"
              options={airports.filter(
                (a: Airport) => a.id !== filters.departureAirportId,
              )}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              value={
                airports.find((a) => a.id === filters.arrivalAirportId) || null
              }
              onChange={(e, value) =>
                handleChange({
                  target: {
                    name: "arrivalAirportId",
                    value: value ? value.id : "",
                  },
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    "app.main.flights.searchPanel.inputLabel2",
                    "Arrival at the airport",
                  )}
                  variant="outlined"
                />
              )}
            />
          </div>
        </FormControl>
      </div>

      {/* from */}
      <div className={classes.row}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            label={t(
              "app.main.schedules.searchPanel.datepicker.from",
              "From date",
            )}
            format="dd/MM/yyyy"
            inputVariant="outlined"
            size="small"
            disableToolbar
            autoOk
            variant="inline"
            value={fromDate}
            onChange={(e) => {
              setSelectedFromDate(e);
              dispatch(
                filterFlights({
                  ...filters,
                  page: 0,
                  fromDate: DateUtils.convertDateToIso(e),
                }),
              );
            }}
          />
        </MuiPickersUtilsProvider>
        {filters.fromDate && (
          <>
            &nbsp;
            <IconButton
              style={{ position: "absolute", right: "0.7rem" }}
              onClick={() => {
                handleChange({ target: { name: "fromDate", value: "" } });
                setSelectedFromDate(null);
              }}
              size="small"
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </>
        )}
      </div>

      {/* to */}
      <div className={classes.row}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            label={t("app.main.schedules.searchPanel.datepicker.to", "To")}
            format="dd/MM/yyyy"
            inputVariant="outlined"
            size="small"
            minDate={fromDate}
            disableToolbar
            autoOk
            variant="inline"
            value={toDate}
            onChange={(e) => {
              setSelectedToDate(e);
              dispatch(
                filterFlights({
                  ...filters,
                  page: 0,
                  toDate: DateUtils.convertDateToIso(e),
                }),
              );
            }}
          />
        </MuiPickersUtilsProvider>
        {filters.toDate && (
          <>
            &nbsp;
            <IconButton
              style={{ position: "absolute", right: "0.7rem" }}
              onClick={() => {
                handleChange({ target: { name: "toDate", value: "" } });
                setSelectedToDate(null);
              }}
              size="small"
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </>
        )}
      </div>

      {/* NEW: Bulk price update button (BEFORE Clear All) */}
      <Button
        variant="contained"
        className={classes.bulkButton}
        color="primary"
        disabled={!canOpenModal}
        onClick={() => setUpdateModalOpen(true)}
      >
        {t(
          "app.main.flights.searchPanel.button.bulkPriceUpdate",
          "Bulk price update",
        )}
      </Button>

      {/* Clear All */}
      <Button
        variant="outlined"
        onClick={() => {
          dispatch(clearfilterFlights());
          dispatch(filterFlights({ page: 0 }));
          dispatch(fetchAllAirports({ size: 9999 }));
        }}
      >
        {t("app.main.schedules.searchPanel.button.clear", "Clear All")}
      </Button>
      {isLocationMainFlightList && (
        <Button
          variant="outlined"
          onClick={() => history.push("/flights/edit-prices")}
        >
          {t("app.main.flights.searchPanel.button.editPrices", "Edit prices")}
        </Button>
      )}

      {/* NEW: Modal */}
      <Dialog
        open={updateModalOpen}
        onClose={() => !submitting && setUpdateModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("app.main.flights.searchPanel.modal.title", "Update route prices")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            {t(
              "app.main.flights.searchPanel.modal.summary",
              "This will update prices for the filtered flights (one-way & return) in the selected date range.",
            )}
          </Typography>

          <TextField
            label={t("app.main.flights.searchPanel.modal.route", "Route")}
            fullWidth
            margin="dense"
            value={
              depAirport && arrAirport
                ? `${depAirport.code}-${depAirport.name} → ${arrAirport.code}-${arrAirport.name}`
                : ""
            }
            InputProps={{ readOnly: true }}
            variant="outlined"
          />

          <TextField
            label={t(
              "app.main.flights.searchPanel.datepicker.from",
              "From date",
            )}
            fullWidth
            margin="dense"
            value={
              filters.fromDate ? DateUtils.convertDate(filters.fromDate) : ""
            }
            InputProps={{ readOnly: true }}
            variant="outlined"
          />

          <TextField
            label={t("app.main.schedules.searchPanel.datepicker.to", "To")}
            fullWidth
            margin="dense"
            value={filters.toDate ? DateUtils.convertDate(filters.toDate) : ""}
            InputProps={{ readOnly: true }}
            variant="outlined"
          />

          <TextField
            label={t(
              "app.main.flights.searchPanel.modal.priceAdjustment",
              "Price adjustment",
            )}
            type="number"
            fullWidth
            margin="dense"
            value={priceAdjustment}
            onChange={(e) =>
              setPriceAdjustment(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            helperText={t(
              "app.main.flights.searchPanel.modal.priceAdjustment.help",
              "Positive to increase, negative to decrease (e.g., 400 or -400).",
            )}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUpdateModalOpen(false)}
            disabled={submitting}
          >
            {t("app.shared.cancel", "Cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleBulkUpdate}
            disabled={submitting || priceAdjustment === ""}
          >
            {submitting
              ? t("app.shared.saving", "Saving...")
              : t("app.shared.update", "Update")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SearchPanel;
